import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import HttpErrorResponse from '../interfaces/common/httpErrorResponse';
import Paging from '../interfaces/common/paging';
import Seller from '../interfaces/output/seller';
import Pagination from '../interfaces/common/pagination';
import { RootState } from '../setup';

export type SellerState = {
    seller: Seller;
    isLoading: boolean;
    isLoadingById: boolean;
    sellers: Pagination<Seller>;
    error: HttpErrorResponse | undefined;
    isUpdating: boolean;
    isDeleting: boolean;
    isCreating: boolean;
};

const initialState: SellerState = {
    seller: {} as Seller,
    sellers: { content: [] as Array<Seller> } as Pagination<Seller>,
    isLoading: false,
    error: undefined,
    isLoadingById: false,
    isUpdating: false,
    isDeleting: false,
    isCreating: false
};

export const sellerSlice = createSlice({
    name: 'seller',

    initialState,
    reducers: {
        fetchSellers: (state: SellerState, _action: PayloadAction<{ paging: Paging }>) => {
            state.isLoading = true;
        },
        fetchSellersSuccess: (state: SellerState, action: PayloadAction<Pagination<Seller>>) => {
            state.isLoading = false;
            state.sellers = action.payload;
        },
        fetchSellersError: (state: SellerState, action: PayloadAction<HttpErrorResponse>) => {
            state.isLoading = false;
            state.error = action.payload;
        },
        fetchSellerById: (state: SellerState, _action: PayloadAction<number>) => {
            state.isLoadingById = true;
        },
        fetchSellerByIdSuccess: (state: SellerState, action: PayloadAction<Seller>) => {
            state.isLoadingById = false;
            state.seller = action.payload;
        },
        fetchSellerByIdError: (state: SellerState, action: PayloadAction<HttpErrorResponse>) => {
            state.isLoadingById = false;
            state.error = action.payload;
        },
        resetSeller: (state: SellerState) => {
            state.seller = initialState.seller;
        },
        resetSellers: (state: SellerState) => {
            state.sellers = initialState.sellers;
        },
        createSeller: (state: SellerState, _action: PayloadAction<Seller>) => {
            state.isCreating = true;
        },
        createSellerSuccess: (state: SellerState, action: PayloadAction<Seller>) => {
            state.isCreating = false;
            state.seller = action.payload;
        },
        createSellerError: (state: SellerState, action: PayloadAction<HttpErrorResponse>) => {
            state.isCreating = false;
            state.error = action.payload;
        },
        updateSeller: (state: SellerState, _action: PayloadAction<Seller>) => {
            state.isUpdating = true;
        },
        updateSellerSuccess: (state: SellerState, action: PayloadAction<Seller>) => {
            state.isUpdating = false;
            state.seller = action.payload;
        },
        updateSellerError: (state: SellerState, action: PayloadAction<HttpErrorResponse>) => {
            state.isUpdating = false;
            state.error = action.payload;
        },
        deleteSeller: (state: SellerState, _action: PayloadAction<Seller>) => {
            state.isDeleting = true;
        },
        deleteSellerSuccess: (state: SellerState, action: PayloadAction<Seller>) => {
            state.isDeleting = false;
            state.seller = action.payload;
        },
        deleteSellerError: (state: SellerState, action: PayloadAction<HttpErrorResponse>) => {
            state.isDeleting = false;
            state.error = action.payload;
        }
    }
});

export const { fetchSellers, fetchSellersSuccess, fetchSellersError, fetchSellerById, fetchSellerByIdSuccess,
    fetchSellerByIdError, resetSeller, resetSellers, createSeller, createSellerError, createSellerSuccess,
    updateSeller, updateSellerError, updateSellerSuccess, deleteSeller, deleteSellerError, deleteSellerSuccess } = sellerSlice.actions;

export const isLoadingSellers = (state: RootState): boolean => state.sellerReducer.isLoading;
export const getSellers = (state: RootState): Pagination<Seller> => state.sellerReducer.sellers;
export const getSeller = (state: RootState): Seller => state.sellerReducer.seller;
export const isLoadingSeller = (state: RootState): boolean => state.sellerReducer.isLoadingById;
export const isCreatingSeller = (state: RootState): boolean => state.sellerReducer.isCreating;
export const isUpdatingSeller = (state: RootState): boolean => state.sellerReducer.isUpdating;
export const isDeletingSeller = (state: RootState): boolean => state.sellerReducer.isDeleting;

export default sellerSlice.reducer;